import type { PrescriptionDoctorApiOutContract } from '../api/generated/pwrdbyQuickMDProvidersApi.schemas'

// The backend returns a top-level `Status` (script lifecycle: rx_change_*,
// rx_renewal_*, printed, xmit, etc.) but the OpenAPI-derived
// PrescriptionDoctorApiOutContract has not been regenerated to include it
// yet. Declare the runtime shape here so consumers stay typed without
// blocking CI on out-of-sync generated types.
export type PrescriptionWithStatus = PrescriptionDoctorApiOutContract & {
  Status?: string | null
}

type LegacyPrescription = {
  Brand: string
  CreatedAt: string
  Directions: string
  DispenseAmount: number
  DispenseUnits: string
  Doctor: { FirstName: string; LastName: string; Designation: string }
  Dosage: string
  DrugName: string
  Duration: string
  Frequency: string
  PrescriptionId: string
  Note: string
  Pharmacy: PrescriptionDoctorApiOutContract['Pharmacy']
  PharmacyPartner: PrescriptionDoctorApiOutContract['PharmacyPartner'] | null
  Quantity: string
  Refills: number
  Type: 'order' | 'treat'
  Status: string
  ShippingAddress: {
    StreetAddress1?: string
    StreetAddress2?: string
    City?: string
    StateCode?: string
    Zip?: string
  } | null
  Shipment: { Carrier: string; ShippedDate: string; TrackingNumber: string; TrackingUrl: string } | null
  UpdatedAt: string
  IsRefillOrder: boolean
  RefillsRemaining: number
  EncounterUrl: string | undefined
  TotalFillsRemaining: number | null
  LastOrderDate: string | null
}

export type { LegacyPrescription }

/**
 * Maps the new prescription-primary API contract to the shape expected
 * by ui_kit_qmd's Prescription interface (Order, PrescriptionPopUp, etc.)
 */
export function mapPrescriptionToLegacy(rx: PrescriptionWithStatus): LegacyPrescription {
  const lastOrder = rx.LastOrder

  // When there's no order yet, the prescription-primary contract emits
  // "pending" where the legacy order-shaped contract would have emitted
  // "pendingauthorization". ui_kit_qmd's Order/PrescriptionPopUp gate the
  // EncounterUrl link on PENDINGAUTHORIZATION, so translate here.
  const rawStatus =
    lastOrder?.Status ?? (rx.Status?.toLowerCase() === 'pending' ? 'pendingauthorization' : rx.Status) ?? ''

  return {
    Brand: rx.Brand ?? '',
    CreatedAt: rx.CreatedAt ?? '',
    Directions: rx.Directions ?? '',
    DispenseAmount: rx.DispenseAmount ?? 0,
    DispenseUnits: rx.DispenseUnits ?? '',
    Doctor: {
      FirstName: rx.Doctor?.FirstName ?? '',
      LastName: rx.Doctor?.LastName ?? '',
      Designation: rx.Doctor?.Designation ?? '',
    },
    Dosage: rx.Dosage ?? '',
    DrugName: rx.DrugName ?? '',
    Duration: rx.Duration != null ? String(rx.Duration) : '',
    Frequency: rx.Frequency ?? '',
    PrescriptionId: rx.PrescriptionId ?? '',
    Note: rx.Note ?? '',
    Pharmacy: rx.Pharmacy,
    PharmacyPartner: rx.PharmacyPartner ?? null,
    Quantity: rx.Quantity != null ? String(rx.Quantity) : '',
    Refills: rx.Refills ?? 0,
    Type: (rx.Type ?? 'treat') as 'order' | 'treat',

    // Prefer LastOrder.Status (fulfillment) — fall back to top-level Status
    // (script lifecycle: rx_change_*, rx_renewal_*, printed, xmit, etc.).
    // Uppercased here so all consumers can do a direct OrderStatus lookup.
    Status: rawStatus.toUpperCase(),
    // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
    ShippingAddress: lastOrder?.ShippingAddress ?? null,
    Shipment:
      lastOrder?.Shipment ?
        {
          Carrier: lastOrder.Shipment.Carrier ?? 'usps',
          ShippedDate: lastOrder.Shipment.ShippedDate ?? '',
          TrackingNumber: lastOrder.Shipment.TrackingNumber ?? '',
          TrackingUrl: lastOrder.Shipment.TrackingUrl ?? '',
        }
      : null,
    UpdatedAt: lastOrder?.Date ?? rx.CreatedAt ?? '',
    IsRefillOrder: false,
    RefillsRemaining: rx.TotalFillsRemaining ?? 0,
    EncounterUrl: rx.EncounterUrl ?? undefined,

    // New fields for the updated PrescriptionCard/PrescriptionPopUp
    TotalFillsRemaining: rx.TotalFillsRemaining ?? null,
    LastOrderDate: lastOrder?.Date ?? null,
  }
}
